/**
 * Game feel ที่ Arcade Physics ไม่มีให้ (PHYSICS.md §5)
 *
 * แยกออกมาเป็น state machine ล้วนๆ ไม่แตะ Phaser เพื่อให้ทดสอบได้จริง —
 * เทสต์ P6 ("ปุ่มกระโดดหาย 1 แพ็กเก็ตยังกระโดดได้") ต้องพิสูจน์ได้โดยไม่ต้องเปิดเบราว์เซอร์
 */
import { PHYS } from "@py/protocol";

export type Buttons = {
  l: number; r: number; u: number; d: number;
  /** ปุ่มกระโดดกดค้าง — ใช้คุมความสูงเท่านั้น */
  j: number;
  /** ตัวนับการกด — ใช้ตัดสินใจ "กระโดด" เพราะ MQTT QoS 0 ทำแพ็กเก็ตหายได้ */
  jp: number;
};

export const NO_INPUT: Buttons = { l: 0, r: 0, u: 0, d: 0, j: 0, jp: 0 };

export type FeelState = {
  coyoteMs: number;
  bufferMs: number;
  lastJp: number;
  dropThruMs: number;
  /** นับถอยหลังช่วงที่ห้ามตัดความสูงกระโดด (กันแพ็กเก็ต j:1 หาย) */
  minJumpMs: number;
};

export function newFeelState(jp = 0): FeelState {
  return { coyoteMs: 0, bufferMs: 0, lastJp: jp, dropThruMs: 0, minJumpMs: 0 };
}

export type FeelResult = {
  /** สั่งกระโดดในเฟรมนี้ */
  jump: boolean;
  /** ตัดความสูงกระโดด (ปล่อยปุ่มขณะยังลอยขึ้น) */
  cutJump: boolean;
  /** ปิด collision ด้านบนชั่วคราว เพื่อลงจาก one-way platform */
  dropThru: boolean;
};

/**
 * @param dt มิลลิวินาที
 * @param onGround body.blocked.down
 * @param vy ความเร็วแนวตั้งปัจจุบัน
 */
export function stepFeel(
  st: FeelState,
  input: Buttons,
  dt: number,
  onGround: boolean,
  vy: number,
): FeelResult {
  // coyote — เดินตกขอบแล้วยังกระโดดได้อีกนิด ทำให้ไม่รู้สึกว่า "กดแล้วไม่ติด"
  if (onGround) st.coyoteMs = PHYS.COYOTE_MS;
  else st.coyoteMs -= dt;

  // jump buffer — กดก่อนแตะพื้นนิดเดียวก็ยังนับ
  // อ่านจาก jp (ตัวนับ) ไม่ใช่ j (กดค้าง): ถ้าแพ็กเก็ตที่มี j:1 หายไปเฟรมเดียว
  // การกระโดดจะหายไปเลย แต่ตัวนับที่เปลี่ยนค่ายังอ่านได้จากแพ็กเก็ตถัดไป
  if (input.jp !== st.lastJp) {
    st.bufferMs = PHYS.BUFFER_MS;
    st.lastJp = input.jp;
  } else {
    st.bufferMs -= dt;
  }

  let jump = false;
  let dropThru = false;

  if (st.bufferMs > 0 && st.coyoteMs > 0) {
    // กดลง + กระโดด = ตั้งใจลงจากกิ่ง ไม่ใช่กระโดดขึ้น (ฐาน 5 มีกิ่ง 4 ระดับ)
    if (input.d === 1 && onGround) {
      dropThru = true;
      st.dropThruMs = PHYS.DROP_THRU_MS;
    } else {
      jump = true;
    }
    st.bufferMs = 0;
    st.coyoteMs = 0;
  }

  if (st.dropThruMs > 0) st.dropThruMs -= dt;

  // ห้ามตัดความสูงในช่วงต้นของการกระโดด — แพ็กเก็ต j:1 หายเฟรมเดียวไม่ควรทำให้
  // การกระโดดเหลือ 45% (ที่ฐาน 2 จะข้ามอิฐสูง 120 px ไม่ได้เลย)
  if (jump) st.minJumpMs = PHYS.MIN_JUMP_MS;
  else if (st.minJumpMs > 0) st.minJumpMs -= dt;

  return {
    jump,
    cutJump: input.j === 0 && vy < 0 && st.minJumpMs <= 0,
    dropThru: dropThru || st.dropThruMs > 0,
  };
}

/** ความเร็วแนวนอนเป้าหมายของโหมด platformer */
export function targetVx(input: Buttons): number {
  return (input.r - input.l) * PHYS.RUN;
}

/** โหมด topdown — normalize ทแยงไม่ให้เดินเฉียงเร็วกว่าเดินตรง */
export function topdownVelocity(input: Buttons): { vx: number; vy: number } {
  const dx = input.r - input.l;
  const dy = input.d - input.u;
  if (dx === 0 && dy === 0) return { vx: 0, vy: 0 };
  const len = Math.hypot(dx, dy);
  return { vx: (dx / len) * PHYS.TD_SPEED, vy: (dy / len) * PHYS.TD_SPEED };
}

// ---------- hop ของโหมด topdown ----------
// เจ้าของงานสั่ง "กระโดดได้ทุกฉาก" — ฉากมองบน (maze) ไม่มีแกน y ให้กระโดดจริง
// จึงทำเป็น hop เชิงภาพ: sprite เด้งขึ้นเป็นพาราโบลา ตัวชนอยู่กับที่
// (แตะ body ไม่ได้ — y ของ body คือ "ความลึกในห้อง" ไม่ใช่ความสูง)

export const HOP_MS = 360;
export const HOP_PX = 60;

export type HopState = { lastJp: number; leftMs: number };

export function newHopState(): HopState {
  return { lastJp: -1, leftMs: 0 };
}

/**
 * เดินสถานะ hop หนึ่งเฟรม — คืนระยะยกของ sprite (px, บวก = ลอยขึ้น)
 * อ่านจาก `jp` (ตัวนับ) เหตุผลเดียวกับ stepFeel: QoS 0 แพ็กเก็ตหายได้
 */
export function stepHop(st: HopState, jp: number, dtMs: number): number {
  if (st.lastJp === -1) st.lastJp = jp; // เฟรมแรกหลัง spawn — อย่านับของค้าง
  if (jp !== st.lastJp && st.leftMs <= 0) st.leftMs = HOP_MS;
  st.lastJp = jp;
  if (st.leftMs <= 0) return 0;
  st.leftMs = Math.max(0, st.leftMs - dtMs);
  const t = 1 - st.leftMs / HOP_MS; // 0..1
  return HOP_PX * 4 * t * (1 - t); // พาราโบลา จุดสูงสุดตรงกลาง
}

// ---------- lane assist ของโหมด topdown ----------
// ทางเดินเขาวงกตกว้าง 1 tile (60px) ตัวชนกว้าง 40 เหลือหลวมข้างละ 10px
// ถ้าไม่ตรงกลางช่องเป๊ะจะเกี่ยวมุมกำแพงตลอด — "เดินทะลุยาก" ที่ผู้เล่นบ่น
// ทางแก้แบบ Pac-Man: กดแกนเดียวเมื่อไหร่ ประคองแกนตั้งฉากเข้ากลางช่องให้เอง

/**
 * ความเร็วประคองเข้ากลางช่อง (px/s) — แรงพอให้เลี้ยวมุมลื่น
 * แต่ไม่แรงจนรู้สึกว่าตัวละครถูกดูด
 */
const SNAP_SPEED = 220;

/**
 * คืนความเร็วแกนตั้งฉากสำหรับประคองเข้ากลางช่อง
 * ใช้เมื่อผู้เล่นกด**แกนเดียว**เท่านั้น — กดทแยงถือว่าตั้งใจคุมเองทั้งสองแกน
 * @param centre พิกัดกึ่งกลางตัวชนบนแกนที่จะประคอง
 * @param tile   ขนาดช่อง (TILE_W/H = 60)
 */
export function laneSnapVelocity(centre: number, tile: number): number {
  const target = (Math.floor(centre / tile) + 0.5) * tile;
  const d = target - centre;
  if (Math.abs(d) < 1.5) return 0;
  // เข้าใกล้แล้วผ่อนลง กันแกว่งข้ามเป้าไปมา
  return Math.sign(d) * Math.min(SNAP_SPEED, Math.abs(d) * 12);
}
